import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Settings, ArrowLeft } from 'lucide-react';
// TASK-164 Phase 2: Add NotificationBell
import NotificationBell from '../dashboard/alerts/NotificationBell';
// TASK-381: Live alert data from store + WebSocket
import { ALERT_PAGE_SIZE, useAlertStore } from '../../stores/alertStore';
import { useAlertWebSocket } from '../../hooks/useAlertWebSocket';
// TASK-334: Admin panel link
import { useAccount } from '../../contexts/AccountContext';

interface NavTab {
  labelKey: string;
  path: string;
}

// TASK-164 Phase 2: Added Documents and Alerts tabs
// TASK-168: Added translation keys
// TASK-207: Removed My Book tab (now accessible via ViewLevelSelector in Portfolio)
// TASK-359: Added Tasks tab
const tabs: NavTab[] = [
  // The canvas dashboard is the one attention surface — meetings live on it as a
  // summoned widget (TASK-666), so the tab names the surface, not a ghost page.
  // /meetings keeps its redirect for old bookmarks.
  { labelKey: 'tabs.today', path: '/dashboard' },
  { labelKey: 'tabs.clients', path: '/clients' },
  { labelKey: 'tabs.tasks', path: '/tasks' },
  { labelKey: 'tabs.documents', path: '/documents' },
  { labelKey: 'tabs.alerts', path: '/alerts' },
  { labelKey: 'tabs.portfolio', path: '/portfolio' },
  { labelKey: 'tabs.compliance', path: '/compliance' },
];

const SecondaryNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('navigation');
  const { isAdmin, isComplianceOfficer } = useAccount(); // TASK-334: Admin check, TASK-374: CO check

  // TASK-381: Live alert data
  const alerts = useAlertStore((s) => s.alerts);
  const fetchAlerts = useAlertStore((s) => s.fetchAlerts);
  const fetchCounts = useAlertStore((s) => s.fetchCounts);
  useAlertWebSocket();

  useEffect(() => {
    // TASK-653: the bell's primary count derives from the alert list itself
    // (unresolved GROUPS) — fetch it here so the badge is correct on every
    // page, not only after visiting a page that happened to load alerts.
    fetchAlerts({ limit: ALERT_PAGE_SIZE });
    fetchCounts();
  }, [fetchAlerts, fetchCounts]);

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Back Button + Navigation Tabs */}
          <div className="flex items-center">
            {/* Global Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 mr-6 py-3 px-2 text-sm font-medium text-gray-500 hover:text-kielis-navy transition-colors"
              title="Go back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>

          {/* TASK-748: a named landmark for the primary nav. NOT the app-shell probe —
              `expectAuthenticated` asserts `#root` has children instead, because the
              full-screen routes (`/meeting/:id/review`) render their own chrome and never
              mount this nav at all. */}
          <nav aria-label="Primary" data-testid="app-shell-nav" className="flex space-x-8">
            {tabs.filter((tab) => {
              // TASK-374: CO sees Dashboard, Clients, Compliance, Documents, Portfolio, Tasks
              if (isComplianceOfficer) {
                return ['/dashboard', '/clients', '/compliance', '/documents', '/portfolio', '/tasks'].includes(tab.path);
              }
              return true;
            }).map((tab) => {
              const isActive = location.pathname === tab.path ||
                             (tab.path === '/dashboard' && location.pathname === '/');

              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  className={`
                    py-3 px-1 border-b-2 text-sm font-medium transition-colors
                    ${isActive
                      ? 'border-kielis-gold text-kielis-navy'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {t(tab.labelKey)}
                </Link>
              );
            })}
          </nav>
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-4 py-2">
            {/* TASK-334: Admin Panel Link (visible only to admins) */}
            {isAdmin && (
              <Link
                to="/admin"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-kielis-navy hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Settings size={16} />
                Admin
              </Link>
            )}

            {/* TASK-164 Phase 2: Notification Bell — TASK-381: Live data */}
            <NotificationBell alerts={alerts} maxPreview={3} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecondaryNav;